import {Directive, ElementRef, input, OnDestroy, output, ViewContainerRef} from '@angular/core';
import {ConnectedPosition, Overlay, OverlayConfig, OverlayRef} from '@angular/cdk/overlay';
import {TemplatePortal} from '@angular/cdk/portal';
import {merge, Observable, Subscription} from 'rxjs';
import {Nedtrekksmeny} from "./nedtrekksmeny";
import {outputToObservable} from "@angular/core/rxjs-interop";

export type KonfigurasjonsCallback = (overlay: Overlay, elementRef: ElementRef<HTMLElement>) => OverlayConfig;

/** Trigger for åpning av nedtrekksmeny, se {@link NedtrekksmenyKomponent} */
@Directive({
    selector: '[nedtrekksmenyTriggerFor]',
    host: {
        '(click)': 'togglePanel()'
    },
    standalone: true
})
export class NedtrekksmenyTriggerForDirektiv implements OnDestroy {

    panelLukket = output({alias: 'nedtrekksmenyLukket'});
    panelAapnet = output({alias: 'nedtrekksmenyAapnet'});

    panel = input.required<Nedtrekksmeny>({alias: 'nedtrekksmenyTriggerFor'});
    panelklasse = input('', {alias: 'nedtrekksmenyKlasse'});
    maksBredde = input('', {alias: 'nedtrekksmenyMaksBredde'});
    bredde = input('', {alias: 'nedtrekksmenyBredde'});
    posisjon = input<ConnectedPosition[]>(this.standardPosisjon(), {alias: 'nedtrekksmenyPosisjon'});
    deaktivert = input(false, {alias: 'nedtrekksmenyDeaktivert'});
    /** Mat Overlay konfigurasjon */
    konfigurasjon = input<KonfigurasjonsCallback>(()=>({}), {alias: 'nedtrekksmenyKonfigurasjon'});

    private erÅpen = false;
    private overlayRef: OverlayRef | null = null;
    private panelLukkesAbonnemang = Subscription.EMPTY;

    constructor(private overlay: Overlay, private elementRef: ElementRef<HTMLElement>, private viewContainerRef: ViewContainerRef) {
    }

    togglePanel(): void {
        this.erÅpen ? this.lukkeOgTerminerPanel() : this.åpenPanel();
    }

    åpenPanel(): void {
        if (this.deaktivert()) return;

        this.panelAapnet.emit();
        this.erÅpen = true;
        this.overlayRef = this.overlay.create(this.beregnKonfigurasjon());

        this.overlayRef.attach(new TemplatePortal(
          this.panel().templateRef(),
          this.viewContainerRef
        ));

        this.panelLukkesAbonnemang = this.panelLukkesSomObserverbar()
          .subscribe(() => this.lukkeOgTerminerPanel());
    }

    //private hjelpemetoder

    private beregnKonfigurasjon() {
        let stdKonfig = {
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-transparent-backdrop',
            panelClass: ['nedtrekksmeny-panel-kontainer', this.panelklasse()],
            maxWidth: this.maksBredde(),
            width: this.bredde(),
            scrollStrategy: this.overlay.scrollStrategies.close(),
            positionStrategy: this.overlay
              .position()
              .flexibleConnectedTo(this.elementRef)
              .withGrowAfterOpen(true)
              .withPositions(this.posisjon())
        };
        return {...stdKonfig, ...this.konfigurasjon()(this.overlay, this.elementRef)};}

    ngOnDestroy(): void {
        this.overlayRef?.dispose();
    }

    //private hjelpemetoder

    /** Returnerer en observerbar som leverer verdier ved ulike handlinger som fører til at panel lukkes */
    private panelLukkesSomObserverbar(): Observable<MouseEvent | void> {
        const klikkPåBackdrop = this.overlayRef!.backdropClick();
        const overlayDetached = this.overlayRef!.detachments(); //@todo hvorfor?
        const panelLukket = outputToObservable(this.panel().vedLukket);

        return merge(klikkPåBackdrop, overlayDetached, panelLukket);
    }

    private lukkeOgTerminerPanel(): void {
        this.panelLukket.emit();

        if (!this.overlayRef || !this.erÅpen) return;

        this.panelLukkesAbonnemang.unsubscribe();
        this.erÅpen = false;
        this.overlayRef.detach(); //@todo kalle dispose?
    }

    //angir hvilket punkt på nedtrekksmenyen (overlay) som skal plasseres på hvilket punkt på origin (this.elementRef)
    //the "best" position will be selected based on how well the overlay would fit within the viewport.
    private standardPosisjon(): ConnectedPosition[] {
        return [
            {
                originX: 'end',
                originY: 'bottom',
                overlayX: 'end',
                overlayY: 'top',
                offsetY: 2 //avstand mellom trigge-elementet og panel
            },
            {
                originX: 'start',
                originY: 'bottom',
                overlayX: 'start',
                overlayY: 'top',
                offsetY: 2 //avstand mellom trigge-elementet og panel
            }
        ];
    }
}
